import { api, apiSchemas } from '@verifime/api-definition';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { failureReason, reportLookupFailure } from './workflowMetadataReporting';

type TWorkflowMetadata = z.infer<typeof apiSchemas.WorkflowMetadata>;

/** What a lookup for one customer id came to, bar whether it has settled. */
interface LookupOutcome {
  metadata: TWorkflowMetadata | undefined;
  organisationName: string | undefined;
  error: boolean;
  notFound: boolean;
}

/** The outcome of the request for one customer id, kept with the id it answers. */
interface SettledLookup {
  customerId: string;
  outcome: LookupOutcome;
}

/** What the hook reports while there is no settled outcome for the current customer id. */
const NO_OUTCOME: LookupOutcome = {
  metadata: undefined,
  organisationName: undefined,
  error: false,
  notFound: false,
};

type TWorkflowMetadataItem = NonNullable<
  z.infer<typeof apiSchemas.WorkflowMetadataPageResponse>['itemList']
>[number];

/**
 * The item for the permission granted most recently. A permission with no `grantedAt` counts as the
 * oldest.
 *
 * TECHNICAL DEBT: Frontend sorting - remove when backend supports sorting. The API returns all
 * active permissions for this customer, so they are sorted here by permission.grantedAt descending.
 */
function mostRecentlyGranted(itemList: TWorkflowMetadataItem[]): TWorkflowMetadataItem {
  const grantedAt = (item: TWorkflowMetadataItem) =>
    item.permission?.grantedAt ? new Date(item.permission.grantedAt).getTime() : 0;

  return itemList.toSorted((a, b) => grantedAt(b) - grantedAt(a))[0];
}

/**
 * What a response for one customer comes to: the provider of the permission granted most recently,
 * or not found for an empty list. `undefined` for a response with no `itemList` at all, which is
 * not "found nothing" but a body the hook cannot read.
 */
function outcomeFromResponse(
  data: z.infer<typeof apiSchemas.WorkflowMetadataPageResponse>,
): LookupOutcome | undefined {
  if (!data.itemList) {
    return undefined;
  }
  if (data.itemList.length === 0) {
    return { ...NO_OUTCOME, notFound: true };
  }
  const latest = mostRecentlyGranted(data.itemList);
  return { ...NO_OUTCOME, metadata: latest.metadata, organisationName: latest.organisationName };
}

interface UseWorkflowMetadataByCustomerIdResult {
  metadata: TWorkflowMetadata | undefined;
  organisationName: string | undefined;
  isLoading: boolean;
  error: boolean;
  /**
   * The request succeeded and the customer has no permission to read a provider from: told apart
   * from `error` (the request failed) and from a settled result with a provider.
   */
  notFound: boolean;
}

/**
 * Hook that fetches workflow metadata using a customer ID (from authenticated user context).
 *
 * This hook is designed for authenticated users who may have multiple active permissions
 * with different organisations. The API returns all active permissions, and this hook
 * selects the most recently granted one.
 *
 * TECHNICAL DEBT: Frontend sorting by grantedAt
 * ---------------------------------------------
 * The sorting logic below is a temporary workaround. The backend API currently returns
 * all active permissions without sorting or pagination support.
 *
 * This should be removed when backend implements pagination and sorting support,
 * allowing the API to return the most recent permission directly via query parameters.
 *
 * Related: Backend work needed for pagination/sorting capabilities
 *
 * @param customerId - The customer ID from auth context
 */
export function useWorkflowMetadataByCustomerId(
  customerId: string | null | undefined,
): UseWorkflowMetadataByCustomerIdResult {
  const [settled, setSettled] = useState<SettledLookup | undefined>(undefined);

  useEffect(() => {
    if (!customerId) {
      setSettled(undefined);
      return;
    }

    // A response for a customer id that is no longer current is dropped.
    let isCurrent = true;

    /** Records how this customer's lookup came out, unless the customer id has moved on. */
    const settle = (outcome: LookupOutcome) => {
      if (isCurrent) {
        setSettled({ customerId, outcome });
      }
    };

    /** Records a failed lookup, and reports why. Only a reason is reported, never the error. */
    const settleAsFailed = (reason: string) => {
      if (isCurrent) {
        reportLookupFailure('customer-id', reason);
      }
      settle({ ...NO_OUTCOME, error: true });
    };

    api
      .getV1workflowmetadatacustomerCustomerId({
        params: { customerId },
      })
      .then(
        (data) => {
          const outcome = outcomeFromResponse(data);
          if (outcome) {
            settle(outcome);
          } else {
            settleAsFailed('invalid-response');
          }
        },
        // The request itself failed.
        (err) => settleAsFailed(failureReason(err)),
      )
      // Reading a response that arrived is the only thing left to throw, and it is a defect here,
      // not a failed request: it is told apart in the log.
      .catch(() => settleAsFailed('unexpected-error'));

    return () => {
      isCurrent = false;
    };
  }, [customerId]);

  // Loading is derived from the request state, not set in an effect: it is true from the first
  // render that has a customer id until that customer's response lands, so a caller never reads the
  // render before the fetch starts as a settled, empty result. Only an outcome for the current
  // customer id is read.
  const outcome = settled && settled.customerId === customerId ? settled.outcome : undefined;

  return { ...(outcome ?? NO_OUTCOME), isLoading: !!customerId && !outcome };
}
